import { useState, useEffect, useCallback, useContext } from "react";

// Librerías
import axios from "axios";
import { toast } from "react-hot-toast";

// Componentes de Material UI
import {
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Dialog,
  Button,
  useTheme,
  useMediaQuery,
  LinearProgress,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  FormHelperText,
} from "@mui/material";
import { createStyles } from "@mui/material/styles";

import { LoadingButton } from "@mui/lab";

import { AuthContext } from "../../../../common/middlewares/Auth";

import GetTipoEncuesta from "../Listas/getTipoEncuesta";
import { useGetSurveyStatus } from "../Hooks/useGetSurveyStatus";
import { MobileDatePicker } from "@mui/x-date-pickers";

const stylesTipoRetiro = createStyles(() => ({
  InputBase: {
    color: "#999999",
  },
}));

const ModalCreateRetirement = ({ handleOpenDialog, open, ID, values, refresh, rowData }) => {
  const classes = stylesTipoRetiro();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [flagSubmit, setFlagSubmit] = useState(false);
  const [tipoEncuesta, setTipoEncuesta] = useState("");
  const [dtFechaRetiro, setDtFechaRetiro] = useState(null);

  const authContext = useContext(AuthContext);
  const { token, strInfoUser } = authContext;

  const theme = useTheme();
  const bitMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Hook para obtener estado de encuesta
  const { dataStatus, refreshGetDataStatus } = useGetSurveyStatus({
    autoLoad: false,
    strEmail: rowData?.strEmail,
  });

  const submitData = useCallback(
    async (signalSubmitData) => {
      try {
        setFlagSubmit(false);

        const res = await axios(
          {
            method: "POST",
            baseURL: `${process.env.REACT_APP_PROTOCOL_BACKEND}://${process.env.REACT_APP_HOST_BACKEND}${process.env.REACT_APP_PORT_BACKEND}`,
            url: `${process.env.REACT_APP_API_SETACTIVATERETIREMENT}`,
            params: {
              strEmail: rowData.strEmail,
              strTipoEncuesta: tipoEncuesta,
              strEmailAdmin: strInfoUser?.strEmail,
              strNombreColaborador: rowData.strNombreCompleto,
              dtFechaRetiro: dtFechaRetiro
            },
            headers: {
              token,
            },
          },
          { cancelToken: signalSubmitData.token }
        );

        if (res.data.error) throw new Error(res.data.msg);

        toast.success(res.data.msg);
        setSuccess(true);
      } catch (error) {
        if (!axios.isCancel(error)) {
          const msg = error.response?.data?.msg || error.message;
          console.error(error);
          toast.error(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [tipoEncuesta, rowData?.strEmail, strInfoUser?.strEmail, token, rowData?.strNombreCompleto, dtFechaRetiro]
  );

  // Cargar estado de encuesta cuando se abre el modal
  useEffect(() => {
    if (open && rowData?.strEmail) {
      refreshGetDataStatus({ strEmail: rowData.strEmail });
    }
  }, [open, rowData, refreshGetDataStatus]);

  useEffect(() => {
    if (open) {
      setTipoEncuesta(null);
      setDtFechaRetiro(null);
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    let signalSubmitData = axios.CancelToken.source();

    if (flagSubmit) {
      submitData(signalSubmitData);
    }

    return () => {
      signalSubmitData.cancel("Petición abortada.");
    };
  }, [flagSubmit, submitData]);

  useEffect(() => {
    if (success) {
      if (typeof refresh === "function") {
        refresh();
      }
      handleOpenDialog();
      setSuccess(false);
      setTipoEncuesta(null);
      setDtFechaRetiro(null);
    }
  }, [success, refresh, handleOpenDialog]);

  // Si no hay email válido
  if (!rowData?.strEmail) {
    return (
      <Dialog
        fullScreen={bitMobile}
        open={open}
        onClose={handleOpenDialog}
        PaperProps={{
          style: {
            backgroundColor: !loading && !rowData?.strEmail ? "#FDEDED" : "inherit",
          },
        }}
      >
        <DialogContent>
          {loading ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Alert severity="error">
              <AlertTitle>
                <b>No se encontró el identificador del colaborador</b>
              </AlertTitle>
              Ha ocurrido un error al momento de seleccionar los datos, por favor escala al área de TI para mayor información.
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleOpenDialog} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog fullScreen={bitMobile} open={open} onClose={handleOpenDialog} fullWidth>
      {loading && <LinearProgress />}

      {dataStatus > 0 ? (
        <>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>
                <b>Encuesta activa</b>
              </AlertTitle>
              El colaborador tiene una encuesta activa, pendiente por realizar.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOpenDialog} color="inherit">
              Cerrar
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>¿Deseas activar la encuesta de retiro?</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <DialogContentText>
                  Esta acción activará la encuesta de retiro para el colaborador seleccionado.
                </DialogContentText>
              </Grid>

              <Grid item xs={12}>
                <GetTipoEncuesta
                  value={tipoEncuesta}
                  handlerChangeData={(e) => setTipoEncuesta(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <MobileDatePicker
                  label="Fecha de retiro"
                  value={dtFechaRetiro}
                  format="dd/MM/yyyy"
                  onChange={(date) => {
                    setDtFechaRetiro(date);
                  }}
                  slotProps={{
                    textField: {
                      name: "dtFechaRetiro",
                      fullWidth: true,
                      required: true,
                      InputProps: { style: classes.InputBase }
                    },
                  }}
                />
                {!dtFechaRetiro && (
                  <FormHelperText sx={{ color: "error.main", mt: 1 }}>
                    Debes seleccionar una fecha de retiro
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <LoadingButton
              color="primary"
              loading={loading}
              type="button"
              disabled={!tipoEncuesta || !dtFechaRetiro || loading}
              onClick={() => {
                if (!tipoEncuesta || !dtFechaRetiro || loading) return;
                setLoading(true);
                setFlagSubmit(true);
              }}
            >
              {loading ? "Activando..." : "Activar"}
            </LoadingButton>

            <Button onClick={handleOpenDialog} color="inherit" disabled={loading}>
              Cancelar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ModalCreateRetirement;
